import { FC, useState, Children, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Placeholder from '../../../../images/BundleDetailsPlaceholder.svg';

import styles from './PromotionDetails.module.css';
import { useTranslation } from 'react-i18next';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import { useYesNoTranslation } from '@/pages/Franchisee/Bundles/Active/internal/UseYesNoTranslation';
import { usePromotion, useUpdatePromotionVisibility } from '@/services/promotions/promotions.service';
import { showToast } from '@/components/Toast/Toast';
import SetVisibleModal from '../../Bundles/BundleDetails/SetVisibleModal/SetVisibleModal';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Table from '@/components/Tables/Table/Table';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import { Effect, EffectPricingTypeNameEnum, EffectTypeNameEnum, PricingTypeEnum } from '@/types/promotions.types';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import { Alert, Grid } from '@mui/material';
import { AlertTriangle } from 'react-feather';

import {
  formatPromotionAmountRange,
  formatPromotionPercentRange,
  getColumnDefinition,
  isMissingNumber,
  toFixedAmount,
} from '@/services/promotions/promotions.helper.service';

function getGobackUrl(isCerPromotion: boolean): string {
  return isCerPromotion ? '/promotions/cer' : '/promotions/active';
}

const Promotion: FC = () => {
  const { t } = useTranslation();
  const { promotionId } = useParams();
  const navigateWithToast = useNavigateWithToast();
  const { yesNoTranslation } = useYesNoTranslation();

  const [showVisibilityModal, setShowVisibilityModal] = useState(false);

  const {
    mutate: updatePromotionVisibility,
    isLoading: updatePromotionVisibilityLoading,
    reset: updatePromotionVisibilityReset,
  } = useUpdatePromotionVisibility(promotionId!);

  const { data, isLoading, isError } = usePromotion(promotionId !== undefined, promotionId!);
  const promotionDetails = data?.data;

  const occasion = renderOccasions(t, promotionDetails?.occasions);

  const openVisibilityModal = () => {
    setShowVisibilityModal(true);
  };

  const closeVisibilityModal = () => {
    setShowVisibilityModal(false);
  };

  const handleSetVisibilityClick = () => {
    updatePromotionVisibility(
      {
        isVisible: !promotionDetails?.isVisible,
      },
      {
        onSuccess: () => {
          showToast({
            label: t('promotions.promotionVisibilityUpdated'),
            id: 'editPromotionVisibilitySuccess',
          });
          closeVisibilityModal();
        },
        onError: () => {
          showToast({
            label: t('error.updatingBundle'),
            toastType: ToastTypes.Error,
            id: 'errorEditingPromotionVisibility',
          });
          closeVisibilityModal();
          updatePromotionVisibilityReset();
        },
      },
    );
  };

  if (isError) {
    navigateWithToast('/', {
      label: t('error.fetchingPromotions'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingPromotion',
    });
  }

  function priceOrDiscountValue(effect: Effect) {
    switch (effect.pricingTypeName) {
      case PricingTypeEnum.PERCENT_OFF_PROMOTION:
        return isMissingNumber(effect.percentage) ? 'N/A' : t('promotions.percentOff', { percent: effect.percentage });
      case PricingTypeEnum.AMOUNT_OFF_PROMOTION: {
        const amount = toFixedAmount(effect.amount);
        return amount === null ? 'N/A' : t('promotions.amountOff', { amount });
      }
      case PricingTypeEnum.FIXED_PRICE_PROMOTION: {
        const amount = toFixedAmount(effect.amount);
        return amount === null ? 'N/A' : t('promotions.amountOff', { amount });
      }
      case PricingTypeEnum.VARIABLE_PERCENT_OFF_PROMOTION: {
        const range = formatPromotionPercentRange(effect.minimumPercentage, effect.maximumPercentage);
        return typeof range === 'string' ? range : t('promotions.percentRangeOff', range);
      }
      case PricingTypeEnum.VARIABLE_AMOUNT_OFF_PROMOTION: {
        const range = formatPromotionAmountRange(effect.minimumAmount, effect.maximumAmount);
        return typeof range === 'string' ? range : t('promotions.amountRangeOff', range);
      }
      case PricingTypeEnum.VARIABLE_FIXED_PRICE_PROMOTION: {
        const range = formatPromotionAmountRange(effect.minimumFixedPrice, effect.maximumFixedPrice);
        return typeof range === 'string' ? range : t('promotions.amountRangeOff', range);
      }
      default:
        return null;
    }
  }

  const promoEffectsData = promotionDetails?.effects?.map((effect: Effect) => ({
    ...effect,
    effectType: EffectTypeNameEnum[effect.typeName as keyof typeof EffectTypeNameEnum],
    pricingEffectType: EffectPricingTypeNameEnum[effect.pricingTypeName as keyof typeof EffectPricingTypeNameEnum],
    priceOrDiscount: priceOrDiscountValue(effect),
  }));

  const columnDefinitions = useMemo(() => {
    const showUpchargeColumn =
      promotionDetails?.effects?.filter((effect) => effect.pricingTypeName === 'FixedPricePromotion') || [];
    return getColumnDefinition(t, showUpchargeColumn.length > 0);
  }, [promotionDetails?.effects, t]);

  const isCerPromotion = useMemo(() => promotionDetails?.description === 'CER', [promotionDetails]);
  const isLock = !!promotionDetails?.isLock;
  const showInvisiblePromotionAlert = promotionDetails?.isVisible === false;
  const invisiblePromotionAlertMessage = isLock
    ? t('promotions.adminVisibilityAlert.invisibleLocked')
    : t('promotions.adminVisibilityAlert.invisibleUnlocked');

  return (
    <div className={styles.page}>
      <Header
        title={promotionDetails?.displayName ?? t('promotions.promotionDetails')}
        breadcrumb={{ label: t('breadcrumb.backToActivePromotions'), to: getGobackUrl(isCerPromotion) }}
      />
      <div className={styles.topContainer}>
        <div className={styles.titleRow}>
          <div className={styles.titleGroup}>
            <h2 className={styles.title}>{t('promotions.promotionDetails')}</h2>
            <Tag
              type={promotionDetails?.isVisible ? TagTypes.Completed : TagTypes.Inactive}
              label={promotionDetails?.isVisible ? t('common.visible') : t('common.invisible')}
            />
          </div>

          <Button
            variant={promotionDetails?.isVisible ? ButtonVariants.Secondary : ButtonVariants.Primary}
            onClick={openVisibilityModal}
          >
            {t('editBundle.bundleDetails.setToVisibleButton', {
              visible: promotionDetails?.isVisible ? t('common.invisible') : t('common.visible'),
            })}
          </Button>
        </div>
        {showInvisiblePromotionAlert && (
          <Alert
            icon={<AlertTriangle size={30} color="#E6740A" />}
            severity="warning"
            className={styles.visibilityAlert}
            sx={{
              backgroundColor: '#FAE4D0',
              color: '#3D3D3D',
              border: '1px solid #E6740A',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 400,
              alignItems: 'center',
              padding: '11px 16px',
              minHeight: '42px',
              '& .MuiAlert-icon': {
                color: '#E6740A',
                fontSize: '20px',
                padding: 0,
                marginRight: '12px',
                opacity: 1,
              },
              '& .MuiAlert-message': {
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <div>{invisiblePromotionAlertMessage}</div>
          </Alert>
        )}
        <Card>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" mb={2}>
            <Grid item mr={2}>
              <div>
                <img
                  height={180}
                  src={Placeholder}
                  alt={t('promotions.promotionPlaceholder')}
                  aria-label={t('promotions.promotionPlaceholder')}
                />
              </div>
            </Grid>
            <Grid item sm={8}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <Grid item direction="column" pr={{ md: '2rem', lg: '4rem', xl: '8rem' }}>
                  <Grid item>
                    <div className={styles.promotionDetails}>
                      <strong>{t('promotions.table.name')}: </strong>
                      <span>{promotionDetails?.name || '-'}</span>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.promotionDetails}>
                      <strong>{t('promotions.table.displayName')}: </strong>
                      <span>{promotionDetails?.displayName || '-'}</span>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.promotionDetails}>
                      <strong>{t('promotions.description')}: </strong>
                      <span>{promotionDetails?.description || '-'}</span>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.promotionDetails}>
                      <strong>{t('promotions.table.occasion')}: </strong>
                      <div>
                        <span key={occasion}>{occasion}</span>
                      </div>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.promotionDetails}>
                      <strong>{t('promotions.code')}: </strong>
                      <div>
                        {!promotionDetails?.redemptionCodes?.length ? (
                          <span className={styles.promotionDetails}>-</span>
                        ) : (
                          Children.toArray(
                            promotionDetails.redemptionCodes.map((code, index) => (
                              <>
                                {index > 0 && ', '}
                                <span key={code}>{code}</span>
                              </>
                            )),
                          )
                        )}
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid item direction="column">
                  <Grid item>
                    <div className={styles.secondPromotionDetails}>
                      <strong>{t('promotions.activeStores')}: </strong>
                      <span>{promotionDetails?.activeStoreCount || '-'}</span>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.secondPromotionDetails}>
                      <strong>{t('promotions.combinable')}: </strong>
                      <span>{yesNoTranslation(promotionDetails?.isCombinable) || '-'}</span>
                    </div>
                  </Grid>
                  <Grid item>
                    <div className={styles.secondPromotionDetails}>
                      <strong>{t('promotions.maxUses')}: </strong>
                      <span>{promotionDetails?.maxUses || '-'}</span>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item mt={2}>
                <div className={styles.promotionDetails}>
                  <strong>{t('promotions.restrictions')}: </strong>
                  <span>{promotionDetails?.legalText || '-'}</span>
                </div>
              </Grid>
            </Grid>
          </Grid>

          <div className={styles.tableContainer}>
            <div className={styles.headerWithTooltip}>
              <h3 className={styles.heading}>{t('promotions.effectsHeader')}</h3>
              <Tooltip content={t('promotions.effectsTooltip')} position={PositionTypes.Top} />
            </div>
            <Table
              isLoading={isLoading}
              loadingRowCount={3}
              data={promoEffectsData || []}
              columns={columnDefinitions}
            />
          </div>
        </Card>
      </div>

      <SetVisibleModal
        headerText={promotionDetails?.isVisible ? t('common.setToInvisible?') : t('common.setToVisible?')}
        confirmText={promotionDetails?.isVisible ? t('common.setToInvisible') : t('common.setToVisible')}
        bodyText={
          promotionDetails?.isVisible
            ? t('promotions.visibleModal.setInvisibleText')
            : t('promotions.visibleModal.setVisibleText')
        }
        isOpen={showVisibilityModal}
        onClose={closeVisibilityModal}
        onConfirm={handleSetVisibilityClick}
        isLoading={updatePromotionVisibilityLoading}
      />
    </div>
  );
};

export default Promotion;
